import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useCallback } from 'react';
import { Save, Trash2, TestTube, CheckCircle, XCircle, AlertCircle, Loader2, MessageSquare, Eye, EyeOff, Key } from 'lucide-react';
import { API_URL } from '../utils/config';

const DEFAULT_LANGUAGES = [
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' }
];

const SpokiTemplatesManager = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [testing, setTesting] = useState({});
  const [status, setStatus] = useState(null);
  const [editedUrls, setEditedUrls] = useState({});
  const [editedSecrets, setEditedSecrets] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  const [testResults, setTestResults] = useState({});
  const [error, setError] = useState(null);

  const fetchTemplates = useCallback(async () => {
    try {
      const token = localStorage.getItem('hw_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/spoki/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
        setStatus({
          enabled: data.spoki_enabled,
          secretConfigured: data.spoki_secret_configured,
          defaultConfigured: data.default_webhook_configured
        });
        setError(null);
        
        // Initialize edited URLs and secrets
        const urls = {};
        const secrets = {};
        data.templates?.forEach(t => {
          urls[t.language_code] = t.webhook_url || '';
          secrets[t.language_code] = ''; // Don't show real secret, user must re-enter
        });
        setEditedUrls(urls);
        setEditedSecrets(secrets);
      } else {
        // API failed - use default languages
        console.error('API returned non-OK status:', response.status);
        setError(`Errore API: ${response.status}`);
        initializeDefaults();
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setError(`Errore: ${error.message}`);
      initializeDefaults();
    } finally {
      setLoading(false);
    }
  }, []);
  
  const initializeDefaults = () => {
    // Initialize with default languages so user can still configure
    const defaultTemplates = DEFAULT_LANGUAGES.map(lang => ({
      language_code: lang.code,
      language_name: lang.name,
      flag: lang.flag,
      webhook_url: '',
      secret_configured: false,
      secret_masked: '',
      is_active: false,
      source: 'none'
    }));
    setTemplates(defaultTemplates);
    
    const urls = {};
    const secrets = {};
    DEFAULT_LANGUAGES.forEach(lang => {
      urls[lang.code] = '';
      secrets[lang.code] = '';
    });
    setEditedUrls(urls);
    setEditedSecrets(secrets);
  };

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSave = async (langCode) => {
    setSaving(prev => ({ ...prev, [langCode]: true }));
    setTestResults(prev => ({ ...prev, [langCode]: null }));
    
    try {
      const token = localStorage.getItem('hw_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/spoki/templates/${langCode}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          language_code: langCode,
          webhook_url: editedUrls[langCode] || '',
          secret: editedSecrets[langCode] || null  // null = keep existing
        })
      });
      
      if (response.ok) {
        setTestResults(prev => ({ ...prev, [langCode]: { success: true, message: 'Salvato!' } }));
        // Clear secret field after save
        setEditedSecrets(prev => ({ ...prev, [langCode]: '' }));
        fetchTemplates();
      } else {
        const error = await response.json();
        setTestResults(prev => ({ ...prev, [langCode]: { success: false, message: error.detail } }));
      }
    } catch (error) {
      setTestResults(prev => ({ ...prev, [langCode]: { success: false, message: error.message } }));
    } finally {
      setSaving(prev => ({ ...prev, [langCode]: false }));
    }
  };

  const handleDelete = async (langCode) => {
    if (!window.confirm(`Sei sicuro di voler rimuovere il template ${langCode.toUpperCase()}?`)) {
      return;
    }
    
    setSaving(prev => ({ ...prev, [langCode]: true }));
    
    try {
      const token = localStorage.getItem('hw_token') || localStorage.getItem('token');
      await fetch(`${API_URL}/api/spoki/templates/${langCode}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setEditedUrls(prev => ({ ...prev, [langCode]: '' }));
      setEditedSecrets(prev => ({ ...prev, [langCode]: '' }));
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
    } finally {
      setSaving(prev => ({ ...prev, [langCode]: false }));
    }
  };

  const handleTest = async (langCode) => {
    setTesting(prev => ({ ...prev, [langCode]: true }));
    setTestResults(prev => ({ ...prev, [langCode]: null }));
    
    try {
      const token = localStorage.getItem('hw_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/spoki/test-template/${langCode}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const result = await response.json();
      setTestResults(prev => ({ ...prev, [langCode]: result }));
    } catch (error) {
      setTestResults(prev => ({ ...prev, [langCode]: { success: false, message: error.message } }));
    } finally {
      setTesting(prev => ({ ...prev, [langCode]: false }));
    }
  };

  const toggleShowSecret = (langCode) => {
    setShowSecrets(prev => ({ ...prev, [langCode]: !prev[langCode] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <MessageSquare className="w-6 h-6 text-green-500" />
        <h2 className="text-xl font-semibold text-slate-800">Template WhatsApp Spoki</h2>
      </div>
      
      {/* Status Banner */}
      <div className={`p-4 rounded-lg ${status?.secretConfigured ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
        <div className="flex items-center gap-2">
          {status?.secretConfigured ? (
            <CheckCircle className="w-5 h-5 text-green-500" />
          ) : (
            <AlertCircle className="w-5 h-5 text-yellow-500" />
          )}
          <span className="font-medium">
            {status?.secretConfigured 
              ? 'Spoki Secret configurato correttamente'
              : 'SPOKI_SECRET non configurato nel .env'}
          </span>
        </div>
        <p className="mt-1 text-sm text-slate-600">
          Configura un webhook URL e un secret per ogni lingua. Il sistema invierà automaticamente il messaggio 
          nella lingua dell'ospite (rilevata dal prefisso telefonico).
        </p>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-800 mb-2">Come configurare:</h3>
        <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
          <li>Crea un template WhatsApp su Spoki per ogni lingua</li>
          <li>Crea un'automazione Spoki che usa quel template</li>
          <li>Copia il <strong>Webhook URL</strong> dell'automazione qui sotto</li>
          <li>Copia il <strong>Secret</strong> dell'automazione (ogni automazione ha il suo!)</li>
          <li>Usa il pulsante "Test" per verificare</li>
        </ol>
      </div>

      {/* Templates List */}
      <div className="space-y-4">
        {templates.map((template) => {
          const langCode = template.language_code;
          const isEdited = editedUrls[langCode] !== (template.webhook_url || '') || 
                          (editedSecrets[langCode] && editedSecrets[langCode].length > 0);
          const result = testResults[langCode];
          
          return (
            <div 
              key={langCode}
              className={`p-4 rounded-lg border ${
                template.is_active 
                  ? 'bg-white border-green-200' 
                  : 'bg-slate-50 border-slate-200'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{template.flag}</span>
                  <div>
                    <span className="font-medium text-slate-800">
                      {template.language_name}
                    </span>
                    <span className="ml-2 text-xs text-slate-500 uppercase">
                      {langCode}
                    </span>
                  </div>
                  {template.is_active && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                      Attivo
                    </span>
                  )}
                  {template.secret_configured && (
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1">
                      <Key className="w-3 h-3" />
                      Secret OK
                    </span>
                  )}
                  {template.source === 'env' && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                      da .env
                    </span>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  {template.is_active && (
                    <button
                      onClick={() => handleTest(langCode)}
                      disabled={testing[langCode]}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 disabled:opacity-50"
                    >
                      {testing[langCode] ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <TestTube className="w-4 h-4" />
                      )}
                      Test
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleSave(langCode)}
                    disabled={saving[langCode] || !isEdited}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving[langCode] ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Salva
                  </button>
                  
                  {template.webhook_url && (
                    <button
                      onClick={() => handleDelete(langCode)}
                      disabled={saving[langCode]}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              
              <div className="space-y-3">
                {/* Webhook URL */}
                <div>
                  <label className="block text-sm text-slate-600 mb-1">
                    Webhook URL Automazione Spoki
                  </label>
                  <input
                    type="url"
                    value={editedUrls[langCode] || ''}
                    onChange={(e) => setEditedUrls(prev => ({ ...prev, [langCode]: e.target.value }))}
                    placeholder="https://app.spoki.it/webhook/..."
                    autoComplete="off"
                    data-lpignore="true"
                    data-form-type="other"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                {/* Secret */}
                <div>
                  <label className="block text-sm text-slate-600 mb-1 flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    Secret Automazione
                    {template.secret_configured && (
                      <span className="text-xs text-green-600">
                        (configurato: {template.secret_masked})
                      </span>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type={showSecrets[langCode] ? "text" : "password"}
                      value={editedSecrets[langCode] || ''}
                      onChange={(e) => setEditedSecrets(prev => ({ ...prev, [langCode]: e.target.value }))}
                      placeholder={template.secret_configured ? "Lascia vuoto per mantenere quello esistente" : "Incolla il secret dell'automazione Spoki"}
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-form-type="other"
                      className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => toggleShowSecret(langCode)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showSecrets[langCode] ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Test Result */}
              {result && (
                <div className={`mt-3 p-3 rounded-lg ${
                  result.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`}>
                  <div className="flex items-center gap-2">
                    {result.success ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <XCircle className="w-4 h-4" />
                    )}
                    <span className="text-sm">
                      {result.message || result.error}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Fallback Info */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
        <h3 className="font-medium text-slate-700 mb-2">Logica di Fallback</h3>
        <p className="text-sm text-slate-600">
          Se un ospite ha un prefisso telefonico per cui non esiste un template configurato:
        </p>
        <ul className="mt-2 text-sm text-slate-600 list-disc list-inside">
          <li>Prima prova: Template della lingua rilevata</li>
          <li>Fallback 1: Template Italiano (IT)</li>
          <li>Fallback 2: Template Inglese (EN)</li>
          <li>Fallback 3: Primo template disponibile</li>
        </ul>
      </div>
    </div>
  );
};

export default SpokiTemplatesManager;
